import { DialogTitle } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useToast } from '@retail/hooks';
import {
  AddItemToAssortmentDialogContent,
  AddToAssortmentFunction,
} from '@retail/my-assortment/components';
import { useAssortmentItemsActions } from '@retail/my-assortment/context';
import { AssortmentItemPriorityDTO } from '@retail/my-assortment/types';
import { Supplier } from '@retail/products/types';
import { Dialog } from '@shared/components';
import { useCallback } from 'react';
import { withDialogSuspenseErrorBoundary } from '@retail/components';

interface Props {
  open: boolean;
  mgItemNumber: number;
  supplierId?: number;
  availableSuppliers?: Supplier[];
  onClose: () => void;
}

function AddItemToAssortment({
  open,
  mgItemNumber,
  supplierId,
  availableSuppliers,
  onClose,
}: Props) {
  const t = useAppTFunction();
  const toast = useToast();
  const { isBrandContext } = useSelectedOrgUnit();

  const { addItemsToAssortment } = useAssortmentItemsActions();

  const handleAddItem: AddToAssortmentFunction = useCallback(
    ({ selectedSupplierId, fromDate, toDate, priority, isStocked }) => {
      const items: AssortmentItemPriorityDTO[] = [
        {
          mgSupplierId: selectedSupplierId ?? supplierId,
          mgItemNumber,
          priority,
          isStocked: isBrandContext ? undefined : isStocked,
          validFrom: fromDate.toISOString(),
          validTo: toDate ? toDate.toISOString() : undefined,
        },
      ];

      addItemsToAssortment(items)
        .then(() => {
          onClose();
        })
        .catch(() => toast.error(t('products.errors.generic')));
    },
    [supplierId, mgItemNumber, isBrandContext, addItemsToAssortment, onClose, toast, t]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('products.mgItem.addItemsToAssortment.addToAssortment')}</DialogTitle>
      <AddItemToAssortmentDialogContent
        availableSuppliers={availableSuppliers}
        supplierId={supplierId}
        includeStocked={!isBrandContext}
        handleAddItem={handleAddItem}
        onClose={onClose}
      />
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(AddItemToAssortment);
